import {getCurrentInstance, onBeforeUnmount, onMounted, ref} from '@vue/composition-api'

import type {MFCleanup} from '@/types/microfrontend'
import {buildMFContext} from '@/utils/microfrontend-context'

/**
 * Composable for mounting React microfrontends in Vue components
 *
 * Handles:
 * - Safe mounting/unmounting lifecycle
 * - Race condition prevention
 * - Cleanup on component unmount
 *
 * @example
 * ```typescript
 * const {containerRef, mount} = useMicrofrontend()
 *
 * const mountComponent = async () => {
 *   const context = buildMFContext(instance, '/path')
 *   await mount(loadYourMF, {...context, props: {}})
 * }
 *
 * onMounted(mountComponent)
 * ```
 */
export const useMicrofrontend = () => {
  const containerRef = ref<HTMLElement | null>(null)
  const unmountFn = ref<MFCleanup | null>(null)
  const isMounted = ref(false)
  const instance = getCurrentInstance()

  /**
   * Mount a microfrontend component
   *
   * @param loader - Async function that loads and mounts the MF
   * @param context - Context object with props
   * @returns Promise that resolves when mounted
   */
  const mount = async <TContext>(
    loader: (element: HTMLElement, context: TContext) => Promise<MFCleanup | null>,
    context: TContext,
  ): Promise<void> => {
    if (!containerRef.value || !instance) return

    try {
      const cleanup = await loader(containerRef.value, context)

      // Guard against race condition: component unmounted during import
      if (!isMounted.value) {
        cleanup?.()
        return
      }

      unmountFn.value = cleanup
    } catch (err) {
      console.error('Error mounting microfrontend:', err)
      throw err
    }
  }

  onMounted(() => {
    isMounted.value = true
  })

  onBeforeUnmount(() => {
    isMounted.value = false
    if (unmountFn.value) {
      unmountFn.value()
      unmountFn.value = null
    }
  })

  return {
    containerRef,
    unmountFn,
    isMounted,
    mount,
    instance,
  }
}

/**
 * Composable for mounting React microfrontends with loading/error states
 *
 * Extends useMicrofrontend with UI state management for better UX
 *
 * @example
 * ```typescript
 * const {containerRef, loading, error, mount, retry} = useMicrofrontendWithState()
 *
 * const mountComponent = async () => {
 *   const context = buildMFContext(instance, '/path')
 *   await mount(loadYourMF, {...context, props: {}})
 * }
 *
 * onMounted(mountComponent)
 * ```
 */
export const useMicrofrontendWithState = () => {
  const base = useMicrofrontend()
  const loading = ref(true)
  const error = ref(false)
  let lastLoader: ((element: HTMLElement, context: unknown) => Promise<MFCleanup | null>) | null = null
  let lastContext: unknown = null

  /**
   * Mount with loading/error state management
   */
  const mount = async <TContext>(
    loader: (element: HTMLElement, context: TContext) => Promise<MFCleanup | null>,
    context: TContext,
  ): Promise<void> => {
    lastLoader = loader as (element: HTMLElement, context: unknown) => Promise<MFCleanup | null>
    lastContext = context as unknown

    loading.value = true
    error.value = false

    try {
      await base.mount(loader, context)
      loading.value = false
    } catch (err) {
      loading.value = false
      error.value = true
      throw err
    }
  }

  /**
   * Retry mounting after error
   */
  const retry = async (): Promise<void> => {
    if (lastLoader && lastContext) {
      await mount(lastLoader, lastContext)
    }
  }

  return {
    ...base,
    loading,
    error,
    mount,
    retry,
  }
}

/**
 * Composable for mounting microfrontends with prop updates
 *
 * Provides update() method for reactive prop changes without unmounting
 *
 * @example
 * ```typescript
 * const {containerRef, buildContext, mountWithUpdate} = useMicrofrontendWithUpdate()
 *
 * const context = buildContext('/path', {
 *   isOpen: props.isOpen,
 *   onClose: () => emit('close')
 * })
 *
 * onMounted(() => mountWithUpdate(loadModal, context))
 *
 * watch(() => props.isOpen, () => {
 *   update(buildContext('/path', {...}))
 * })
 * ```
 */
export const useMicrofrontendWithUpdate = () => {
  const base = useMicrofrontend()

  /**
   * Helper to build context with props
   */
  const buildContext = <TProps>(basePath: string, props: TProps) => {
    if (!base.instance) {
      throw new Error('useMicrofrontendWithUpdate must be called inside setup()')
    }

    const context = buildMFContext(base.instance, basePath)
    return {
      ...context,
      props,
    }
  }

  /**
   * Mount and expose update method
   */
  const mountWithUpdate = async <TContext>(
    loader: (element: HTMLElement, context: TContext) => Promise<MFCleanup | null>,
    context: TContext,
  ): Promise<void> => {
    await base.mount(loader, context)
  }

  /**
   * Update mounted component with new context
   */
  const update = <TContext>(context: TContext): void => {
    if (base.unmountFn.value?.update) {
      base.unmountFn.value.update(context)
    }
  }

  return {
    ...base,
    buildContext,
    mountWithUpdate,
    update,
  }
}
